import { useEffect } from 'react';

type Direction = 'ArrowUp' | 'ArrowDown' | 'ArrowLeft' | 'ArrowRight';

const selectors = [
  '[data-focusable="true"]',
  'button:not([disabled])',
  'a[href]',
  'input:not([disabled])',
  'select:not([disabled])',
].join(',');

function keepsDirectionalKey(target: EventTarget | null, direction: Direction): boolean {
  if (target instanceof HTMLSelectElement) return true;
  if (target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement) {
    return direction === 'ArrowLeft' || direction === 'ArrowRight';
  }
  return false;
}

function activeScope(): HTMLElement | Document {
  const dialogs = Array.from(document.querySelectorAll<HTMLElement>('[aria-modal="true"]'))
    .filter((element) => element.offsetParent !== null);
  return dialogs.at(-1) ?? document;
}

function focusables(scope: HTMLElement | Document): HTMLElement[] {
  return Array.from(scope.querySelectorAll<HTMLElement>(selectors))
    .filter((element) => element.offsetParent !== null && element.tabIndex !== -1);
}

function focusFirst(scope: HTMLElement | Document): void {
  const preferred = scope.querySelector<HTMLElement>('[data-autofocus="true"]');
  (preferred ?? focusables(scope)[0])?.focus({ preventScroll: true });
}

function findNext(current: HTMLElement, direction: Direction, scope: HTMLElement | Document): HTMLElement | null {
  const source = current.getBoundingClientRect();
  const origin = { x: source.left + source.width / 2, y: source.top + source.height / 2 };
  const candidates = focusables(scope).filter((element) => element !== current);

  let best: { element: HTMLElement; score: number } | null = null;
  for (const element of candidates) {
    const rect = element.getBoundingClientRect();
    const point = { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 };
    const dx = point.x - origin.x;
    const dy = point.y - origin.y;
    const inDirection = direction === 'ArrowRight' ? dx > 5
      : direction === 'ArrowLeft' ? dx < -5
      : direction === 'ArrowDown' ? dy > 5
      : dy < -5;
    if (!inDirection) continue;

    const primary = direction === 'ArrowLeft' || direction === 'ArrowRight' ? Math.abs(dx) : Math.abs(dy);
    const secondary = direction === 'ArrowLeft' || direction === 'ArrowRight' ? Math.abs(dy) : Math.abs(dx);
    const score = primary + secondary * 2.4;
    if (!best || score < best.score) best = { element, score };
  }
  return best?.element ?? null;
}

export function useSpatialNavigation(enabled = true): void {
  useEffect(() => {
    if (!enabled) return;
    let scope = activeScope();
    let previousFocus: HTMLElement | null = null;
    const syncScope = () => {
      const nextScope = activeScope();
      if (nextScope === scope) return;
      const active = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      if (nextScope !== document) {
        if (scope === document) previousFocus = active;
        scope = nextScope;
        window.requestAnimationFrame(() => focusFirst(scope));
      } else {
        scope = document;
        window.requestAnimationFrame(() => {
          if (previousFocus?.isConnected) previousFocus.focus({ preventScroll: true });
          else focusFirst(document);
          previousFocus = null;
        });
      }
    };
    const observer = new MutationObserver(syncScope);
    observer.observe(document.body, { childList: true, subtree: true });
    if (scope !== document) window.requestAnimationFrame(() => focusFirst(scope));
    const onKeyDown = (event: KeyboardEvent) => {
      const tvKeyCode = event.keyCode || event.which;
      if (event.key === 'Escape' || event.key === 'BrowserBack' || tvKeyCode === 461 || tvKeyCode === 10009) {
        event.preventDefault();
        window.dispatchEvent(new CustomEvent('cyber-movies:back'));
        return;
      }
      if (!['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(event.key)) return;
      const direction = event.key as Direction;
      // Horizontal arrows still edit text and operate range inputs. Vertical
      // arrows must be able to leave a text field on a D-pad-only TV remote.
      if (keepsDirectionalKey(event.target, direction)) return;

      const current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      scope = activeScope();
      if (!current || current === document.body || (scope instanceof HTMLElement && !scope.contains(current))) {
        focusFirst(scope);
        event.preventDefault();
        return;
      }
      const next = findNext(current, direction, scope);
      if (!next) return;
      event.preventDefault();
      next.focus({ preventScroll: true });
      next.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
    };
    window.addEventListener('keydown', onKeyDown);
    return () => {
      observer.disconnect();
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [enabled]);
}
